/**
 * Custom Values Filter Component
 *
 * Provides a dropdown filter for custom_values key:value pairs.
 * Receives discovered custom values as props from the parent chain
 * (SessionsPageContent → CreateAuditSessionDialog → ContentFiltersForm),
 * following the same pattern as the other FilterGroup filters.
 *
 * If no discovered values are available (prefetch failed or BFF doesn't
 * support it yet), the filter shows empty options and the user can still
 * manually type key:value pairs via allowCustomCreate.
 */

import { MultiSelect } from '@soteria/design-system-ve';
import { useCallback, useMemo } from 'react';

import * as styles from '../CreateAuditSessionDialog.css';
import { validateCustomValues } from '../utils/ValidationHelpers';

interface CustomValuesFilterProps {
  customValues: Record<string, string> | undefined;
  disabled: boolean;
  discoveredCustomValues: Record<string, string[]>;
  loading: boolean;
  onCustomValuesChange: (customValues: Record<string, string> | undefined) => void;
}

/**
 * Converts a Record<string, string[]> from discovery API to MultiSelect options
 * Each key becomes a group, each value becomes a selectable option
 */
const getCustomValueOptions = (customValuesKeys: Record<string, string[]>) =>
  Object.entries(customValuesKeys).flatMap(([key, values]) =>
    values.map(value => ({
      id: `${key}:${value}`,
      label: value,
      value: `${key}:${value}`,
      group: key,
    }))
  );

/**
 * Converts a Record<string, string> to selected values format ("key:value" strings)
 */
const getSelectedValues = (customValues: Record<string, string> = {}): string[] => Object.entries(customValues).map(([key, value]) => `${key}:${value}`);

/**
 * Note: customValues uses Record<string, string> (one value per key).
 * Discovery returns Record<string, string[]> but the filter picks one value per key.
 */
export function CustomValuesFilter({ customValues, disabled, discoveredCustomValues, loading, onCustomValuesChange }: CustomValuesFilterProps) {
  const options = useMemo(() => getCustomValueOptions(discoveredCustomValues), [discoveredCustomValues]);
  const selectedValues = useMemo(() => getSelectedValues(customValues), [customValues]);

  const handleSelectionChange = useCallback(
    (selected: string[]) => {
      // Enforce one-value-per-key: when a new value for an existing key is
      // selected, keep only the newest entry (last in the array) for that key.
      // This prevents the UI from showing two checked items for the same key
      // while only one is stored in the Record<string, string>.
      const newCustomValues: Record<string, string> = {};

      selected.forEach(item => {
        const colonIndex = item.indexOf(':');

        if (colonIndex > 0) {
          const key = item.substring(0, colonIndex).trim();
          const value = item.substring(colonIndex + 1).trim();

          if (key && value) {
            newCustomValues[key] = value;
          }
        }
      });

      // Derive the canonical selected list from the deduplicated record so
      // the MultiSelect UI stays in sync (one chip per key).
      const canonicalSelected = Object.entries(newCustomValues).map(([k, v]) => `${k}:${v}`);

      // If the canonical list differs from what MultiSelect sent us, the
      // parent will re-render with the corrected selectedValues via props.
      onCustomValuesChange(canonicalSelected.length > 0 ? newCustomValues : undefined);
    },
    [onCustomValuesChange]
  );

  const handleValidateCustomOption = useCallback((input: string) => validateCustomValues(input, selectedValues), [selectedValues]);

  const handleCustomOptionCreate = useCallback(
    (key: string, value: string) => {
      const currentValues = customValues || {};
      const newValues = {
        ...currentValues,
        [key]: value,
      };

      onCustomValuesChange(newValues);
    },
    [customValues, onCustomValuesChange]
  );

  return (
    <div className={styles.filterGroup}>
      <MultiSelect
        allowCustomCreate
        constrainToViewport
        searchable
        disabled={disabled || loading}
        emptyMessage={loading ? 'Loading custom values...' : 'No custom values available.'}
        label="Custom Values"
        options={options}
        placeholder={loading ? 'Loading...' : 'Select or add key:value...'}
        searchPlaceholder="Search or add key:value..."
        selectedValues={selectedValues}
        validateCustomOption={handleValidateCustomOption}
        onCustomOptionCreate={handleCustomOptionCreate}
        onSelectionChange={handleSelectionChange}
      />
    </div>
  );
}
